import type { TypeFromSelection } from 'groqd'
import { useNextSanityImage } from 'next-sanity-image'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { twMerge } from 'tailwind-merge'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import { runQuery, sanityClientLike } from '@/cms/client'
import { getFlexibleImageSchema, illustrationQuery, imageAssetQuery } from '@/cms/image'
import type { FlexibleImage as FlexibleImageType, imageAsset } from '@/cms/image'
import { reportError } from '@/services/errorReporting'
import { getLocaleParts } from '@/utils/locale'
import type { PortableTextTypeComponentProps } from '@portabletext/react'
import { useQuery } from '@tanstack/react-query'

import * as styles from './styles'

const getDesktopImage = async (
  flexibleImage: FlexibleImageType,
  language: Language,
  market: SupportedMarketKey
): Promise<{ _type: 'image'; asset: TypeFromSelection<typeof imageAsset> } | null> => {
  let firstImage = flexibleImage.desktopImage?.[0]
  if (!firstImage) {
    return null
  }
  if (firstImage._type === 'reference') {
    firstImage = await runQuery(illustrationQuery, {
      id: firstImage._ref,
    })
    if (!firstImage) {
      return null
    }
  }

  if (firstImage._type === 'image' && 'asset' in firstImage) {
    const currentAsset = firstImage.asset
    if ('_type' in currentAsset && currentAsset._type === 'reference') {
      const asset = await runQuery(imageAssetQuery, { id: currentAsset._ref })
      return {
        ...firstImage,
        asset,
      }
    } else if ('url' in currentAsset) {
      return {
        ...firstImage,
        asset: currentAsset,
      }
    }
  } else if (firstImage._type === 'illustration' && 'imageFiles' in firstImage) {
    return (
      firstImage.imageFiles?.filter(
        (image) =>
          (image.market === market || image.market === 'all') &&
          (image.language === language || image.language === 'all')
      )[0]?.image ?? null
    )
  }
  return null
}

export const FlexibleImage = (props: {
  node: PortableTextTypeComponentProps<unknown>
  containerWidth: number
  slideToEdge?: boolean
  itemsCount?: number
  hasPriority: boolean
}) => {
  const { locale } = useRouter()
  const [language, market] = getLocaleParts(locale)
  const flexibleImage = useMemo(
    () => getFlexibleImageSchema(language).parse(props.node.value),
    [language, props.node.value]
  )
  const { data: desktopImage } = useQuery({
    queryKey: ['flexibleImage', language, market, JSON.stringify(flexibleImage.desktopImage)],
    queryFn: async () => getDesktopImage(flexibleImage, language, market),
  })
  const maxWidth = Math.min(flexibleImage.imageWidth ?? props.containerWidth, props.containerWidth)
  // We use a custom builder here instead of useNextSanityImage since we dont' want
  // these images to be automatically transformed to WebP
  const imageProps = useNextSanityImage(sanityClientLike, desktopImage ?? null, {
    imageBuilder: (imageUrlBuilder) =>
      desktopImage
        ? imageUrlBuilder
            .width(maxWidth * 2)
            .height(Math.round((maxWidth * 2) / desktopImage.asset.metadata.dimensions.aspectRatio))
        : imageUrlBuilder,
  })

  const alt = useMemo(() => {
    if ('alt' in flexibleImage) {
      if (typeof flexibleImage.alt === 'string') {
        return flexibleImage.alt ?? ''
      } else if (flexibleImage.alt && language in flexibleImage.alt) {
        return flexibleImage.alt[language] ?? ''
      }
    }
    return ''
  }, [flexibleImage, language])
  if (!imageProps || !desktopImage) {
    return null
  }

  if (desktopImage._type === 'image' && 'asset' in desktopImage) {
    const hasWidth = Boolean(flexibleImage.imageWidth)
    return (
      <Image
        key={imageProps.src}
        className={twMerge(
          'h-auto',
          !hasWidth && '!relative object-cover',
          styles.flexibleImageStyle({
            hasWidth,
            isFirst: props.node.index === 0,
            isLast: props.node.index === (props.itemsCount ?? 0) - 1,
            slideToEdge: props.slideToEdge ?? false,
          })
        )}
        style={{
          maxWidth: maxWidth,
        }}
        {...imageProps}
        width={hasWidth ? imageProps.width : undefined}
        height={hasWidth ? imageProps.height : undefined}
        sizes={`(max-width: ${maxWidth}px) 100vw, ${maxWidth}px`}
        fill={!hasWidth}
        blurDataURL={desktopImage.asset.metadata.lqip}
        placeholder="blur"
        alt={alt}
        loading={props.hasPriority ? 'eager' : 'lazy'}
      />
    )
  }
  reportError(
    new Error(`Assertion: not implemented image with type ${desktopImage._type}`),
    desktopImage
  )
  return null
}
